import {PatientData} from "../../upload/upload-model";
import {Analysis, DoctorFindings, emptyDoctorFindings} from "../analysis";

export interface SleepReportData {
  externalFindings: SleepExternalFindings;
  findings: DoctorFindings;
  metadata: SleepReportMetadata;
}

export interface SleepReportMetadata {
  measurementId: number;
  uniqueId?: string;
  code?: string;
  patientData?: PatientData;
  bmi?: number;
  essScore?: number;
  deviceSerialNumber?: string;
  reportLanguage: string;
  doctor: SleepReportDoctor;
}

export interface SleepReportDoctor {
  firstName?: string;
  lastName?: string;
  title?: string;
  city?: string;
}

export interface SleepExternalFindings {
  measurements: SleepReportMeasurements;
  images: SleepReportImages;
}

export interface SleepReportImages {
  oxygenSaturationTrend?: string;
  sampleImages: SleepReportSampleImage[];
}

export interface SleepReportSampleImage {
  name?: string;
  image: string;
}

export interface SleepReportMeasurements {
  startTime: string;
  totalSleepTime: string;
  ahiIndex: string;
  odi3Index: string;
  positions: SleepReportPositions;
  o2Levels: SleepReportO2Levels;
  snoringDuration: string;
  snoringPercentage: string;
  averageBreathingFrequency: string;
  cheyneStokesDuration: string;
  cheyneStokesPercentage: string;
  hrAverage: string;
  hrMin: string;
  hrMax: string;
}

export interface SleepReportPositions {
  supine: SleepReportPosition;
  notSupine: SleepReportPosition;
  left: SleepReportPosition;
  right: SleepReportPosition;
  prone: SleepReportPosition;
  upright: SleepReportPosition;
}

export interface SleepReportPosition {
  sleepTimeDuration: string;
  sleepTimePercentage: string;
  ahiIndex: string;
  oaAmount: string;
  caAmount: string;
  maAmount: string;
  hypoAmount: string;
  deSatAmount: string;
  oaIndex: string;
  caIndex: string;
  maIndex: string;
  hypoIndex: string;
  deSatIndex: string;
}

export interface SleepReportO2Levels {
  spO2Average: string;
  spO2Minimum: string;
  spO2Baseline: string;
  deSatBelow90: string;
  deSatBelow80: string;
  spO2Duration: string;
  spO2Percentage: string;
}

export function sleepReportDataFrom(analysis: Analysis): SleepReportData {
  return {
    externalFindings: analysis.sleepExternalFindings ?? emptySleepReportData().externalFindings,
    findings: analysis.doctorFindings,
    metadata: {
      measurementId: analysis.id,
      uniqueId: analysis.uniqueId,
      code: analysis.code,
      patientData: analysis.patientData,
      bmi: analysis.bmi,
      essScore: analysis.essScore,
      deviceSerialNumber: analysis.deviceSerialNumber,
      reportLanguage: analysis.reportLanguage,
      doctor: analysis.doctor
    }
  };
}

export function emptySleepReportData(): SleepReportData {
  return {
    findings: emptyDoctorFindings(),
    metadata: {
      measurementId: 0,
      reportLanguage: "",
      doctor: {}
    },
    externalFindings: {
      measurements: {
        startTime: "",
        totalSleepTime: "",
        ahiIndex: "",
        odi3Index: "",
        positions: {
          supine: emptySleepReportPosition(),
          notSupine: emptySleepReportPosition(),
          left: emptySleepReportPosition(),
          right: emptySleepReportPosition(),
          prone: emptySleepReportPosition(),
          upright: emptySleepReportPosition()
        },
        o2Levels: {
          spO2Average: "",
          spO2Minimum: "",
          spO2Baseline: "",
          deSatBelow90: "",
          deSatBelow80: "",
          spO2Duration: "",
          spO2Percentage: ""
        },
        snoringDuration: "",
        snoringPercentage: "",
        averageBreathingFrequency: "",
        cheyneStokesDuration: "",
        cheyneStokesPercentage: "",
        hrAverage: "",
        hrMin: "",
        hrMax: ""
      },
      images: {
        sampleImages: []
      }
    }
  };
}

function emptySleepReportPosition(): SleepReportPosition {
  return {
    sleepTimeDuration: "",
    sleepTimePercentage: "",
    ahiIndex: "",
    oaAmount: "",
    caAmount: "",
    maAmount: "",
    hypoAmount: "",
    deSatAmount: "",
    oaIndex: "",
    caIndex: "",
    maIndex: "",
    hypoIndex: "",
    deSatIndex: ""
  };
}
